import { observer } from "mobx-react";
import moment from "moment";
import * as React from "react";

import LicenceModalEmail from "@client/components/modules/LicenceModal/Email";
import LicenceModalInputForm from "@client/components/modules/LicenceModal/InputForm";
import LicenceModalStatus from "@client/components/modules/LicenceModal/Status";
import Modal from "@client/components/ui/Modal";
import licenceImage from "@client/components/ui/sharedImages/licence.svg";
import customerStore from "@client/stores/CustomerStore";
import licenceStore from "@client/stores/LicenceStore";
import notificationStore from "@client/stores/NotificationStore";
import tagStore from "@client/stores/TagStore";
import { InputField as InputFieldType } from "@typings/InputField";
import {
  Licence,
  LicenceInput,
  normalizeLicenceVersion,
} from "@typings/Licence";

import inputFieldsSchema, { TAG_TRIALS } from "./InputFields";
import licensorStore from "@client/stores/LicensorStore";
import permissions from "@server/api/permissions";

type State = {
  inputFields: InputFieldType[];
  page: "form" | "email" | "status";
  highlightedFields: string[];
  addedLicence: Licence | null;
};
type Props = {
  onRequestClose: () => void;
  licence?: LicenceInput | null;
};

@observer
class LicenceModal extends React.Component<Props, State> {
  state: State = {
    inputFields: [],
    highlightedFields: [],
    page: "form",
    addedLicence: null,
  };

  get isPartner(): boolean {
    return licensorStore.tokenInfo!.roles.some(r =>
      permissions.PARTNER_ROLES.some(pr => pr === r.name),
    );
  }

  UNSAFE_componentWillMount() {
    licenceStore.load();
    const inputFields = [...inputFieldsSchema.FIELDS];
    inputFieldsSchema.applyPolicyLicenceVersions(
      inputFields,
      licensorStore.partnerPolicy,
    );
    this.setState({ inputFields }, () => {
      let stateSetCount = 0;
      const stateSetCallback = () => {
        stateSetCount += 1;
        if (stateSetCount === 2 && this.props.licence) {
          this.fillLicenceInput(this.props.licence);
        }
      };
      this.loadCustomers(stateSetCallback);
      this.loadTags(stateSetCallback);
    });
    this.refreshPartnerPolicy();
  }

  async refreshPartnerPolicy() {
    try {
      await licensorStore.refreshPartnerPolicy();
    } catch (err) {
      return;
    }

    const { inputFields } = this.state;
    inputFieldsSchema.applyPolicyLicenceVersions(
      inputFields,
      licensorStore.partnerPolicy,
    );
    this.setState({ inputFields: [...inputFields] });
  }

  fillLicenceInput(licence: LicenceInput) {
    const inputFields = this.state.inputFields.map(f => {
      const value = licence[f.name as keyof LicenceInput];
      if (f.name === "type") {
        return { ...f, value: value != null ? value === "floating" : false };
      }
      if (f.name === "version") {
        // recreating a licence keeps its edition, defaulting older licences
        // which predate the SAP edition to Standard:
        return { ...f, value: normalizeLicenceVersion(value) };
      }
      if (value != null) {
        if (f.type === "date") {
          return {
            ...f,
            value: moment
              .utc(moment(value as string).format("YYYY-MM-DD"))
              .toDate(),
          };
        }
        return { ...f, value };
      }
      return { ...f };
    });

    inputFieldsSchema.handleTagTrialTypeChange(
      inputFields,
      TAG_TRIALS.some(
        t => t === inputFields.find(f => f.name === "tag")?.value,
      ),
    );

    // re-applied after the licence's own edition was filled in, so recreating
    // an SAP licence under a partner which may not issue them falls back to
    // the Standard edition rather than carrying an unusable value:
    inputFieldsSchema.applyPolicyLicenceVersions(
      inputFields,
      licensorStore.partnerPolicy,
    );

    const tagValue = inputFields.find(f => f.name === "tag")?.value;
    const supportField = inputFields.find(f => f.name === "support");
    if (supportField) {
      supportField.hidden = tagValue !== "Production";
    }

    this.setState({ inputFields });
  }

  async loadCustomers(stateSetCallback: () => void) {
    const orgInputField = this.state.inputFields.find(
      f => f.name === "customer_id",
    );
    if (!orgInputField) {
      return;
    }
    await customerStore.load();
    orgInputField.noItemsMessage = "No customers found";
    orgInputField.enum = customerStore.customers.map(org => ({
      label: org.name,
      value: org.id,
    }));
    this.setState(
      { inputFields: [...this.state.inputFields] },
      stateSetCallback,
    );
  }

  async loadTags(stateSetCallback: () => void) {
    const tagInputField = this.state.inputFields.find(f => f.name === "tag");
    if (!tagInputField) {
      return;
    }
    await tagStore.load();
    tagInputField.noItemsMessage = "Tags load error";
    tagInputField.enum = tagStore.tags.map(tag => ({
      label: tag.name,
      value: tag.name,
      color: tag.color,
    }));
    this.setState(
      { inputFields: [...this.state.inputFields] },
      stateSetCallback,
    );
  }

  async handleGenerateClick() {
    const messages: string[] = [];
    const highlightedFields = this.state.inputFields
      .filter(f => {
        if (f.validate) {
          const result = f.validate(f, this.state.inputFields);
          if (typeof result === "string") {
            messages.push(result);
            return true;
          }
          return !result;
        }
        if (!f.required || f.value) {
          return false;
        }
        return true;
      })
      .map(f => f.name);
    this.setState({ highlightedFields });
    if (highlightedFields.length) {
      if (messages.length) {
        notificationStore.alert(messages[0], "error");
      }
      return;
    }
    const licence = inputFieldsSchema.buildData(
      this.state.inputFields,
      licenceStore.licences,
      customerStore.customers,
    );

    let customerId = licence.customer_id;
    if (licence.new_customer) {
      const addedCustomer = await customerStore.add({
        ...licence.new_customer,
      });
      if (!addedCustomer) {
        return;
      }
      customerId = addedCustomer.id;
    }
    const licenceInput = { ...licence, customer_id: customerId };
    const addedLicence = await licenceStore.add(licenceInput);
    if (!addedLicence) {
      return;
    }
    this.setState({ page: "email", addedLicence });
  }

  async handleSendClick() {
    if (!this.state.addedLicence) {
      throw new Error("No added licence");
    }

    const successful = await licenceStore.send(this.state.addedLicence.id);
    if (successful) {
      this.setState({ page: "status" });
    }
  }

  get hideTypeToggle(): boolean {
    const policy = licensorStore.partnerPolicy;
    return (
      (policy?.allowed_licence_types?.length ?? 0) > 0 &&
      !policy!.allowed_licence_types!.includes("floating")
    );
  }

  handleFieldChange(field: InputFieldType, value: any) {
    const inputFields = this.state.inputFields.map(f =>
      f.name === field.name ? { ...f, value } : { ...f },
    );

    if (field.name === "tag") {
      const isTrialTag = TAG_TRIALS.includes(value);
      inputFieldsSchema.handleTagTrialTypeChange(inputFields, isTrialTag);
      if (!isTrialTag) {
        inputFieldsSchema.applyTagEndDate(inputFields, value);
        inputFieldsSchema.resetQuantityDefaults(inputFields);
        inputFieldsSchema.applyPolicyTagDefaults(
          inputFields,
          value,
          licensorStore.partnerPolicy,
        );
      }
      const supportField = inputFields.find(f => f.name === "support");
      if (supportField) {
        supportField.hidden = value !== "Production";
        if (supportField.hidden) {
          supportField.value = null;
        }
      }
    }

    this.setState({ inputFields });
  }

  handleNewCustomerNameChange(name: string) {
    const inputFields = this.state.inputFields.map(f =>
      f.name === "customer_id" ? { ...f, enumNewValue: name } : { ...f },
    );
    this.setState({ inputFields });
  }

  renderPage() {
    const customer = customerStore.customers.find(o =>
      this.state.addedLicence
        ? o.id === this.state.addedLicence.customer_id
        : false,
    );

    switch (this.state.page) {
      case "form":
        return (
          <LicenceModalInputForm
            highlightedFields={this.state.highlightedFields}
            inputFields={this.state.inputFields}
            hideTypeToggle={this.hideTypeToggle}
            onFieldChange={(field, value) => {
              this.handleFieldChange(field, value);
            }}
            onNewCustomerNameChange={name => {
              this.handleNewCustomerNameChange(name);
            }}
            onGenerateClick={() => {
              this.handleGenerateClick();
            }}
            onRequestClose={this.props.onRequestClose}
            loading={
              licenceStore.updating ||
              licenceStore.generating ||
              customerStore.updating
            }
            licenceImage={licenceImage}
          />
        );
      case "email":
        return (
          <LicenceModalEmail
            onRequestClose={this.props.onRequestClose}
            sending={licenceStore.sending}
            onSendClick={() => {
              this.handleSendClick();
            }}
            licenceImage={licenceImage}
            licenceText={licenceStore.generatedPem}
            customer={customer || null}
            licence={this.state.addedLicence}
          />
        );
      case "status":
        return (
          <LicenceModalStatus onRequestClose={this.props.onRequestClose} />
        );
      default:
        return null;
    }
  }

  render() {
    return (
      <Modal
        isOpen
        title="New Coriolis Licence"
        onRequestClose={this.props.onRequestClose}
      >
        {this.renderPage()}
      </Modal>
    );
  }
}

export default LicenceModal;
